<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { push, link } from "svelte-spa-router";
  import {
    fetchDashboardLive,
    getInfraGitStatus,
    getCheckAlive,
    runCheckAlive,
    ApiError,
    type InfraGitStatus,
    type CheckAliveResult,
  } from "../lib/api";
  import { session } from "../lib/session.svelte";
  import UserLink from "../lib/UserLink.svelte";
  import type {
    DashboardLive,
    DashboardStudent,
  } from "@diy-avatar/shared/admin";

  // --- live monitoring snapshot ---
  let live: DashboardLive | null = null;
  let loaded = false;
  let error: string | null = null;
  let stale = false;
  let paused = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  // Ticks once a second so "updated Xs ago" stays current between polls.
  let nowMs = Date.now();
  let clock: ReturnType<typeof setInterval> | null = null;

  // --- infra: server git status ---
  let gitStatus: InfraGitStatus | null = null;
  let gitError: string | null = null;

  // --- infra: health (check-alive) ---
  let checkAlive: CheckAliveResult | null = null;
  let checkRunning = false;
  let checkError: string | null = null;

  // NOTE (D48): admin-triggered updates were REMOVED (docker-socket updater AND
  // host systemd updater) after repeated container->root escape findings. There
  // is no update or channel-switch state here any more — updates run over SSH.

  /** "up 1h 23m", "15m", "3m", "12s". */
  function formatDuration(seconds: number): string {
    const s = Math.max(0, Math.floor(seconds));
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return `${sec}s`;
  }

  function uptimeText(onlineSince: number): string {
    return `up ${formatDuration((nowMs - onlineSince) / 1000)}`;
  }

  function sessionText(connectedSince: number | null): string {
    if (connectedSince == null) return "—";
    return formatDuration((nowMs - connectedSince) / 1000);
  }

  function fmtClock(ms: number): string {
    return new Date(ms).toLocaleTimeString();
  }

  $: updatedAgo = live ? Math.max(0, Math.round((nowMs - live.generated_at) / 1000)) : null;

  /** A student row links to /users/:id unless its identity is the unknown fallback. */
  function isUnknownStudent(s: DashboardStudent): boolean {
    return s.id === s.display_name;
  }

  async function refresh() {
    try {
      live = await fetchDashboardLive();
      loaded = true;
      error = null;
      stale = false;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        push("/");
        return;
      }
      // Keep last-good data on screen; just flag staleness.
      error = err instanceof Error ? err.message : "Could not load.";
      stale = true;
    }
  }

  function manualRefresh() {
    void refresh();
  }

  function togglePause() {
    paused = !paused;
  }

  async function refreshGit() {
    try {
      gitStatus = await getInfraGitStatus();
      gitError = null;
    } catch (err) {
      gitError = err instanceof Error ? err.message : "Could not load git status.";
    }
  }

  async function loadCheckAlive() {
    try {
      checkAlive = await getCheckAlive();
      checkError = null;
    } catch (err) {
      checkError = err instanceof Error ? err.message : "Could not load health.";
    }
  }

  async function rerunCheckAlive() {
    checkRunning = true;
    checkError = null;
    try {
      checkAlive = await runCheckAlive();
    } catch (err) {
      checkError = err instanceof Error ? err.message : "check-alive failed";
    } finally {
      checkRunning = false;
    }
  }

  function statusClass(s: "OK" | "WARN" | "FAIL"): string {
    return s === "OK" ? "ok" : s === "WARN" ? "warn" : "fail";
  }

  function healthClass(s: "ok" | "warn" | "fail"): string {
    return s;
  }

  function fmtRanAt(ms: number | null): string {
    if (ms == null) return "—";
    return new Date(ms).toLocaleString();
  }

  function dateTime(s: string | null | undefined): string {
    if (!s) return "";
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleString();
  }

  onMount(() => {
    if (!$session.user) {
      push("/");
      return;
    }
    void refresh();
    void refreshGit();
    void loadCheckAlive();
    timer = setInterval(() => {
      if (!paused) void refresh();
    }, 5000);
    clock = setInterval(() => (nowMs = Date.now()), 1000);
  });

  onDestroy(() => {
    if (timer) clearInterval(timer);
    if (clock) clearInterval(clock);
  });
</script>

<section>
  <!-- Tier 0: header bar -->
  <div class="header" data-testid="dashboard-header">
    <h1>Live monitoring</h1>
    <div class="header-controls">
      <span class="updated" class:stale data-testid="updated-ago">
        {#if updatedAgo == null}
          loading…
        {:else if stale}
          stale — last update {updatedAgo}s ago
        {:else}
          updated {updatedAgo}s ago
        {/if}
      </span>
      <button on:click={manualRefresh} data-testid="dashboard-refresh">Refresh</button>
      <button
        class:active={paused}
        on:click={togglePause}
        data-testid="dashboard-pause"
        aria-pressed={paused}
      >
        {paused ? "Resume" : "Pause"}
      </button>
    </div>
  </div>

  {#if error && !loaded}
    <p class="error" data-testid="error">{error}</p>
  {/if}

  {#if !loaded}
    <p data-testid="empty">Loading…</p>
  {:else if live}
    <!-- Tier 1: attention / alerts (hidden when empty) -->
    {#if live.attention.length > 0}
      <div class="attention" data-testid="attention-panel">
        {#each live.attention as item, i (i)}
          <a
            href={item.href}
            use:link
            class="attn-item {item.severity}"
            data-testid="attention-item-{i}"
          >
            <span class="attn-sev">{item.severity}</span>
            <span class="attn-msg">{item.message}</span>
          </a>
        {/each}
      </div>
    {/if}

    <!-- Tier 2: KPI strip -->
    <div class="kpis" data-testid="kpi-strip">
      <div class="kpi" data-testid="kpi-devices">
        <div class="kpi-val">{live.devices_online}<span class="kpi-tot">/{live.devices_total}</span></div>
        <div class="kpi-label">Devices online</div>
      </div>
      <div class="kpi" data-testid="kpi-students">
        <div class="kpi-val">{live.students_connected}</div>
        <div class="kpi-label">Students connected</div>
      </div>
      <div class="kpi" data-testid="kpi-rooms">
        <div class="kpi-val">{live.rooms_active}</div>
        <div class="kpi-label">Rooms active</div>
      </div>
      <div class="kpi" data-testid="kpi-updates">
        <div class="kpi-val" class:warn={live.updates_available > 0}>{live.updates_available}</div>
        <div class="kpi-label">Updates available</div>
      </div>
      <div class="kpi" data-testid="kpi-encrypted">
        <div class="kpi-val">{live.encrypted.enabled}<span class="kpi-tot">/{live.encrypted.total}</span></div>
        <div class="kpi-label">Encrypted</div>
      </div>
      <div class="kpi" data-testid="kpi-health">
        <div class="kpi-val">
          <span class="badge {healthClass(live.health.status)}">{live.health.status}</span>
        </div>
        <div class="kpi-label">Server health · checked {fmtClock(live.health.checked_at)}</div>
      </div>
    </div>

    <!-- Tier 3: two live lists -->
    <div class="lists">
      <div class="card list" data-testid="online-devices">
        <h2>Online devices</h2>
        <ul class="rows">
          {#each live.online_devices as d (d.label)}
            <li class="row" data-testid="device-row-{d.label}">
              <span class="dot online"></span>
              <div class="row-main">
                <div class="row-title">
                  <a href="/devices/{encodeURIComponent(d.label)}" use:link>{d.label}</a>
                  {#if d.encrypted}
                    <span class="lock" title="Encrypted" aria-label="encrypted">🔒</span>
                  {/if}
                  {#if d.update_available}
                    <span class="badge warn" title="Update available">update</span>
                  {/if}
                  {#if d.idle}
                    <span class="idle" data-testid="device-idle-{d.label}">idle</span>
                  {/if}
                  <span class="uptime">{uptimeText(d.online_since)}</span>
                </div>
                <div class="row-sub">
                  {#if d.room_id != null}
                    <a href="/rooms/{d.room_id}" use:link>{d.room_name ?? `room ${d.room_id}`}</a>
                  {:else}
                    <span class="muted">no room</span>
                  {/if}
                  <span class="sep">·</span>
                  {#if d.school_id != null}
                    <a href="/schools/{d.school_id}" use:link>{d.school_name ?? "school"}</a>
                  {:else}
                    <span class="muted">no school</span>
                  {/if}
                  <span class="sep">·</span>
                  <span>{d.students_connected.length} students</span>
                </div>
              </div>
            </li>
          {/each}
          {#if live.online_devices.length === 0}
            <li class="empty" data-testid="devices-empty">No devices online</li>
          {/if}
        </ul>
      </div>

      <div class="card list" data-testid="connected-students">
        <h2>Connected students</h2>
        <ul class="rows">
          {#each live.connected_students as s (s.id)}
            <li class="row" data-testid="student-row-{s.id}">
              <span class="dot online"></span>
              <div class="row-main">
                <div class="row-title">
                  {#if isUnknownStudent(s)}
                    <span class="unknown" title="Unknown identity">{s.display_name}</span>
                  {:else}
                    <UserLink id={s.id} displayName={s.display_name} size={20} />
                  {/if}
                  <span class="uptime">{sessionText(s.connected_since)}</span>
                </div>
                <div class="row-sub">
                  {#if s.room_id != null}
                    <a href="/rooms/{s.room_id}" use:link>{s.room_name ?? `room ${s.room_id}`}</a>
                  {:else}
                    <span class="muted">no room</span>
                  {/if}
                  <span class="sep">·</span>
                  {#if s.school_id != null}
                    <a href="/schools/{s.school_id}" use:link>{s.school_name ?? "school"}</a>
                  {:else}
                    <span class="muted">no school</span>
                  {/if}
                  <span class="sep">·</span>
                  <span>{s.device_label ?? "no device"}</span>
                </div>
              </div>
            </li>
          {/each}
          {#if live.connected_students.length === 0}
            <li class="empty" data-testid="students-empty">No students connected</li>
          {/if}
        </ul>
      </div>
    </div>

    <!-- Tier 4: ops / infra (demoted) -->
    <details class="ops" data-testid="ops-infra" open>
      <summary>Ops &amp; infrastructure</summary>
      <div class="grid">
        <div class="card" data-testid="card-server-status">
          <h2>Server status</h2>
          {#if gitError}
            <p class="error">{gitError}</p>
          {:else if gitStatus}
            <dl>
              <dt>Backend version</dt>
              <dd data-testid="git-running-version">
                {#if gitStatus.running_version}
                  {gitStatus.running_version}
                  {#if gitStatus.running_commit}
                    <span class="mono">({gitStatus.running_commit.slice(0, 7)})</span>
                  {/if}
                {:else}
                  unknown
                {/if}
              </dd>
              <dt>Deployed</dt>
              <dd data-testid="git-deployed-tag">
                {#if gitStatus.deployed_tag}
                  {gitStatus.deployed_tag}
                  {#if gitStatus.deployed_commit}
                    <span class="mono">({gitStatus.deployed_commit.slice(0, 7)})</span>
                  {/if}
                {:else}
                  <span class="warn">unavailable</span>
                {/if}
              </dd>
              <dt>Deployed at</dt>
              <dd data-testid="git-deployed-at">{gitStatus.deployed_at ? dateTime(gitStatus.deployed_at) : "—"}</dd>
              <dt>Channel</dt>
              <dd data-testid="git-channel">
                {#if gitStatus.channel}
                  {gitStatus.channel}
                {:else}
                  <span class="warn">unavailable</span>
                {/if}
              </dd>
              <dt>Working tree</dt>
              <dd>
                {#if gitStatus.clean === true}
                  <span class="ok">clean</span>
                {:else if gitStatus.clean === false}
                  <span class="warn">dirty</span>
                {:else}
                  <span class="warn">unknown</span>
                {/if}
              </dd>
              <dt>Behind</dt>
              <dd data-testid="git-behind">
                {#if gitStatus.target_tag == null}
                  <span class="warn">no release pinned</span>
                {:else if (gitStatus.releases_behind ?? 0) > 0 || (gitStatus.commits_behind ?? 0) > 0}
                  <span class="warn">
                    {gitStatus.releases_behind} releases,
                    {gitStatus.commits_behind} commits → {gitStatus.target_tag}
                  </span>
                {:else if gitStatus.releases_behind == null || gitStatus.commits_behind == null}
                  <span class="warn">drift unknown ({gitStatus.target_tag})</span>
                {:else}
                  <span class="ok">up to date ({gitStatus.target_tag})</span>
                {/if}
              </dd>
            </dl>
            {#if gitStatus.errors && Object.keys(gitStatus.errors).length > 0}
              <ul class="status-errors" data-testid="git-errors">
                {#each Object.entries(gitStatus.errors) as [field, msg]}
                  <li class="warn"><span class="mono">{field}</span>: {msg}</li>
                {/each}
              </ul>
            {/if}
            <p class="meta" data-testid="update-hint">
              Updates and channel switches are performed manually over SSH:
              <code>bash infra/update.sh</code> on the server.
            </p>
          {:else}
            <p>Loading…</p>
          {/if}
        </div>

        <div class="card" data-testid="card-health">
          <div class="card-head">
            <h2>Health</h2>
            <button
              on:click={rerunCheckAlive}
              disabled={checkRunning}
              data-testid="health-rerun"
            >
              {checkRunning ? "Running…" : "Re-run"}
            </button>
          </div>
          {#if checkError}
            <p class="error">{checkError}</p>
          {/if}
          {#if checkAlive && checkAlive.ran_at !== null}
            <p class="overall">
              Overall:
              <span class="badge {statusClass(checkAlive.overall)}" data-testid="health-overall">
                {checkAlive.overall}
              </span>
              <span class="meta">@ {fmtRanAt(checkAlive.ran_at)}</span>
            </p>
            <ul class="checklist" data-testid="health-services">
              {#each checkAlive.services ?? [] as svc (svc.name)}
                <li>
                  <span class="badge {statusClass(svc.status)}">{svc.status}</span>
                  <strong>{svc.name}</strong>
                  {#if svc.detail}<span class="meta">— {svc.detail}</span>{/if}
                </li>
              {/each}
            </ul>
            <details>
              <summary>Raw output</summary>
              <pre class="raw">{checkAlive.raw}</pre>
            </details>
          {:else if !checkError}
            <p class="meta">Never run.</p>
          {/if}
        </div>
      </div>
    </details>
  {/if}
</section>

<style>
  h1 {
    margin: 0;
  }
  h2 {
    margin: 0 0 0.5rem;
    font-size: 1rem;
    color: #34d399;
  }
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
  }
  .header-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .updated {
    color: #9ca3af;
    font-size: 0.85rem;
  }
  .updated.stale {
    color: #fbbf24;
  }
  button {
    padding: 0.35rem 0.6rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 6px;
    font-size: 0.8rem;
    cursor: pointer;
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  button.active {
    background: #3b2f06;
    border-color: #b45309;
    color: #fbbf24;
  }

  /* Tier 1: attention */
  .attention {
    display: grid;
    gap: 0.35rem;
    margin-bottom: 1rem;
    padding: 0.6rem 0.75rem;
    background: #4c0519;
    border: 1px solid #b91c1c;
    border-radius: 8px;
  }
  .attn-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-decoration: none;
    color: #fecaca;
    font-size: 0.85rem;
  }
  .attn-item:hover .attn-msg {
    text-decoration: underline;
  }
  .attn-sev {
    text-transform: uppercase;
    font-size: 0.65rem;
    font-weight: 700;
    padding: 0.1rem 0.35rem;
    border-radius: 4px;
    background: #7f1d1d;
    color: #fecaca;
  }
  .attn-item.info .attn-sev {
    background: #1e3a5f;
    color: #bfdbfe;
  }
  .attn-item.warn .attn-sev {
    background: #3b2f06;
    color: #fbbf24;
  }
  .attn-item.error .attn-sev {
    background: #7f1d1d;
    color: #fecaca;
  }

  /* Tier 2: KPI strip */
  .kpis {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    margin-bottom: 1rem;
  }
  .kpi {
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
    padding: 0.75rem 1rem;
  }
  .kpi-val {
    font-size: 1.5rem;
    font-weight: 700;
    color: #f3f4f6;
  }
  .kpi-val.warn {
    color: #fbbf24;
  }
  .kpi-tot {
    font-size: 1rem;
    color: #6b7280;
    font-weight: 400;
  }
  .kpi-label {
    color: #9ca3af;
    font-size: 0.8rem;
    margin-top: 0.2rem;
  }

  /* Tier 3: lists */
  .lists {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    margin-bottom: 1.25rem;
  }
  .card {
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
    padding: 0.75rem 1rem;
  }
  .rows {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0.5rem;
  }
  .row {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.4rem 0;
    border-bottom: 1px solid #1f2937;
  }
  .row:last-child {
    border-bottom: none;
  }
  .row-main {
    flex: 1;
    min-width: 0;
  }
  .row-title {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
    font-size: 0.9rem;
  }
  .row-title a {
    color: #34d399;
    text-decoration: none;
    font-weight: 600;
  }
  .row-title a:hover {
    text-decoration: underline;
  }
  .unknown {
    color: #d1d5db;
    font-weight: 600;
  }
  .uptime {
    color: #6b7280;
    font-size: 0.78rem;
    margin-left: auto;
  }
  .lock {
    font-size: 0.8rem;
  }
  .idle {
    color: #fbbf24;
    font-size: 0.72rem;
    text-transform: uppercase;
    font-weight: 700;
  }
  .row-sub {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    flex-wrap: wrap;
    font-size: 0.78rem;
    color: #9ca3af;
    margin-top: 0.15rem;
  }
  .row-sub a {
    color: #6ee7b7;
    text-decoration: none;
  }
  .row-sub a:hover {
    text-decoration: underline;
  }
  .row-sub .sep {
    color: #4b5563;
  }
  .muted {
    color: #6b7280;
  }
  .empty {
    color: #6b7280;
    text-align: center;
    padding: 0.75rem 0;
  }

  /* Tier 4: ops/infra (de-emphasized) */
  .ops {
    margin-top: 0.5rem;
    opacity: 0.85;
  }
  .ops > summary {
    cursor: pointer;
    color: #9ca3af;
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
  }
  .grid {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }
  dl {
    margin: 0 0 0.5rem;
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.2rem 0.75rem;
  }
  dt {
    color: #9ca3af;
  }
  dd {
    margin: 0;
  }
  .card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }
  .card-head h2 {
    margin: 0 0 0.5rem;
  }
  .mono {
    font-family: ui-monospace, monospace;
    color: #9ca3af;
  }
  .badge {
    display: inline-block;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 700;
  }
  .badge.ok,
  .ok {
    color: #6ee7b7;
  }
  .badge.ok {
    background: #064e3b;
  }
  .badge.warn,
  .warn {
    color: #fbbf24;
  }
  .badge.warn {
    background: #3b2f06;
  }
  .badge.fail,
  .fail {
    color: #fca5a5;
  }
  .badge.fail {
    background: #4c0519;
  }
  .overall {
    margin: 0 0 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
  .meta {
    color: #9ca3af;
    font-size: 0.8rem;
  }
  .checklist {
    list-style: none;
    margin: 0 0 0.5rem;
    padding: 0;
    display: grid;
    gap: 0.25rem;
  }
  .checklist li {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.85rem;
  }
  details summary {
    cursor: pointer;
    color: #9ca3af;
    font-size: 0.8rem;
  }
  .raw {
    margin: 0.4rem 0 0;
    padding: 0.5rem;
    background: #0f172a;
    border: 1px solid #1f2937;
    border-radius: 4px;
    font-family: ui-monospace, monospace;
    font-size: 0.75rem;
    color: #d1d5db;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 16rem;
    overflow-y: auto;
  }
  .dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #6b7280;
    margin-top: 0.35rem;
    flex: 0 0 auto;
  }
  .dot.online {
    background: #10b981;
  }
  .error {
    color: #fca5a5;
  }
  .status-errors {
    list-style: none;
    margin: 0.4rem 0 0;
    padding: 0;
    display: grid;
    gap: 0.2rem;
    font-size: 0.75rem;
    color: #9ca3af;
  }
</style>
